import React, { useEffect, useState } from "react";
import StyledButton from "../StyledButton/StyledButton";

type DialogSize = "sm" | "md" | "lg" | "xl";

type DialogProps = {
  title?: string;
  subtitle?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  content?: React.ReactNode;
  size?: DialogSize;
  isFullScreenEnabled?: boolean;
  defaultFullScreen?: boolean;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  isActionButtonVisible?: boolean;
  actionButtonTitle?: string;
  isCancelButtonVisible?: boolean;
  cancelButtonTitle?: string;
  showHeaderDivider?: boolean;
  onClose?: () => void;
  onAction?: () => void;
};

const DIALOG_SIZES: Record<DialogSize, string> = {
  sm: "w-full max-w-[400px] max-h-[90vh]",
  md: "w-full max-w-[600px] max-h-[90vh]",
  lg: "w-full max-w-[800px] max-h-[90vh]",
  xl: "w-full max-w-[1000px] max-h-[90vh]",
};

const Dialog: React.FC<DialogProps> = ({
  title,
  subtitle,
  header,
  footer,
  content,
  size = "md",
  isFullScreenEnabled = false,
  defaultFullScreen = false,
  className = "",
  headerClassName = "",
  contentClassName = "",
  footerClassName = "",
  isActionButtonVisible = true,
  actionButtonTitle = "Save",
  isCancelButtonVisible = true,
  cancelButtonTitle = "Cancel",
  showHeaderDivider = true,
  onClose,
  onAction,
}) => {
  // Internal state to manage fullScreen mode
  const [isFullScreen, setIsFullScreen] = useState(
    isFullScreenEnabled && defaultFullScreen,
  );
  const overlayClass =
    "z-[100] fixed inset-0 w-full h-full bg-black/30 backdrop-blur-sm";

  // Handle click outside
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Sync internal state with prop changes
  useEffect(() => {
    if (isFullScreenEnabled) {
      setIsFullScreen(defaultFullScreen);
    } else {
      setIsFullScreen(false);
    }
  }, [isFullScreenEnabled, defaultFullScreen]);

  const toggleFullScreen = () => {
    if (isFullScreenEnabled) {
      setIsFullScreen(!isFullScreen);
    }
  };

  const renderControlButtons = () => (
    <div className="flex flex-row justify-end w-full gap-2">
      {isCancelButtonVisible && (
        <StyledButton
          id="cancel-button"
          text={cancelButtonTitle}
          className="min-w-[80px] px-6 py-2 text-blue-600 bg-white rounded-sm border border-blue-600 hover:bg-blue-50 transition-colors"
          onClick={onClose}
        />
      )}
      {isActionButtonVisible && (
        <StyledButton
          id="action-button"
          text={actionButtonTitle}
          className="min-w-[80px] px-6 py-2 text-white bg-blue-600 rounded-sm hover:bg-blue-700 transition-colors"
          onClick={onAction}
        />
      )}
    </div>
  );

  const CLOSE_DIALOG_ARIA = "close dialog";
  const TOGGLE_FULLSCREEN_ARIA = "toggle fullscreen";
  const FULLSCREEN_EXIT_ICON = "fullscreen_exit";
  const FULLSCREEN_ICON = "fullscreen";
  const CLOSE_ICON = "close";

  return (
    <div
      className={`${overlayClass} flex items-center justify-center ${isFullScreen ? "p-0" : "p-4"} transition-all duration-200`}
      onClick={handleOverlayClick}
      data-testid="dialog-overlay"
    >
      <div
        className={`${
          isFullScreen
            ? "w-full h-full rounded-none"
            : `${DIALOG_SIZES[size]} rounded-lg`
        } 
          ${className} flex flex-col bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200`}
      >
        {/* Sticky Header */}
        <div
          className={`flex-shrink-0 bg-white ${showHeaderDivider ? "border-b border-gray-200" : ""} ${headerClassName || "px-6 py-5"}`}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              {header ? (
                header
              ) : (
                <div>
                  {title && (
                    <h2 className="text-xl font-semibold text-gray-900 leading-tight">
                      {title}
                    </h2>
                  )}
                  {subtitle && (
                    <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-row  items-center gap-2">
              {isFullScreenEnabled && (
                <button
                  id="toggle-fullscreen-button"
                  className="flex-shrink-0 h-8 w-8 rounded-md hover:bg-gray-100 transition-colors flex items-center justify-center"
                  onClick={toggleFullScreen}
                  aria-label={TOGGLE_FULLSCREEN_ARIA}
                  data-testid="toggle-fullscreen-button"
                >
                  <span className="material-icons text-gray-500 hover:text-gray-700 text-xl">
                    {isFullScreen ? FULLSCREEN_EXIT_ICON : FULLSCREEN_ICON}
                  </span>
                </button>
              )}
              <button
                id="close-button"
                className="flex-shrink-0 h-8 w-8 rounded-md hover:bg-gray-100 transition-colors flex items-center justify-center"
                onClick={onClose}
                data-testid={"close-button"}
                aria-label={CLOSE_DIALOG_ARIA}
              >
                <span className="material-icons text-gray-500 hover:text-gray-700 text-xl">
                  {CLOSE_ICON}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          className={`flex-1 overflow-y-auto ${contentClassName || "px-6 py-6"}`}
        >
          {content}
        </div>

        {/* Sticky Footer */}
        {/* Render footer only if provided or if any control buttons are visible*/}
        {(footer || isActionButtonVisible || isCancelButtonVisible) && (
          <div
            className={`flex-shrink-0 bg-white border-t border-gray-200 ${footerClassName || "px-6 py-4"}`}
          >
            {footer ? footer : renderControlButtons()}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dialog;
